import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useStateWithSessionStorage } from 'hooks';
import Lurker from 'components/atoms/lurker';
import List from 'components/organisms/list';
import ContactUsContainer from 'components/templates/contact-us-container';
import Loading from 'components/atoms/loading';
import Modal from 'components/molecules/modal';
import getLoginToken from 'api/login-token';
import lurkerMetrics from 'util/lurker-metrics';

import { initApolloClient } from 'lib/apollo/withApollo';
import useLoggedUser from 'hooks/useLoggedUser';
import RecordFlattener from 'util/cmsRecordFlattener';
import CatalogsQuery from '../../graphql/queries/catalogs';

type Props = {
  services: any[];
  catalogs: any[];
  redirectFrom: string;
};

const ContactUs = (props: Props) => {
  const flattener = new RecordFlattener();
  const { user } = useLoggedUser();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [stepNumber, setStepNumber] = useState(0);
  const [hasCatalogSelected, setHasCatalogSelected] = useState(false);

  useEffect(() => {
    if (user === null) {
      setShowLoginModal(true);
    }
  }, [user]);

  const filterServicesByLogin = (options: any) => {
    const { services, catalogs } = options;
    const serializedCatalogs = flattener.flatten(catalogs);
    const serializedServices = flattener.flatten(services);

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('userEmail', user?.email ?? '');
    }

    const newServices = user
      ? serializedServices.filter((s: any) => s.is_login_required)
      : serializedServices.filter((s: any) => !s.is_login_required);

    return {
      ...options,
      services: newServices,
      catalogs: user ? serializedCatalogs : [],
    };
  };

  const [, setService] = useStateWithSessionStorage('service');
  // eslint-disable-next-line prettier/prettier
  const [, setSelfService] = useStateWithSessionStorage('selfservice');

  const title = user ? 'Fale com a gente' : 'Não consegue logar?';

  const subtitle = user ? (
    <>Sobre o que você quer falar?</>
  ) : (
    <>Separamos alguns casos que talvez esteja acontecendo com você</>
  );

  const onSelectItem = (item: any, routes: string[] = []) => {
    const routesAll = [...routes, item.name];

    setService({
      name: item.slug,
      externalId: item.external_id,
      hasSelfService: !!item.selfservice,
      contactByChat: item.contact_by_chat,
      routes: routesAll,
      org: item.data_source,
    });

    if (item.selfservice) {
      setSelfService(item.selfservice.slug);
    }
  };

  useEffect(() => {
    if (user) {
      lurkerMetrics('page_view', 'entrou_logado_form', {
        object_name: 'contact_form',
      });
    }
  }, [user]);

  const handleBackButton = () => {
    setStepNumber((state: number) => state - 1);
  };

  const shouldShowBackButton = hasCatalogSelected
    ? handleBackButton
    : undefined;

  return (
    <>
      <Head>
        <title>Fale conosco!</title>
      </Head>

      <Lurker pageType="contact_us" />
      {user === undefined ? (
        <Loading visible />
      ) : (
        <ContactUsContainer
          title={title}
          // @ts-ignore
          subtitle={subtitle}
          backHandler={shouldShowBackButton}
        >
          <List
            {...filterServicesByLogin(props)}
            loggedInUserEmail={user?.email}
            onClick={onSelectItem}
            stepToShow={stepNumber}
            onChangeCatalog={(hasCurrentCatalog: any) => {
              setHasCatalogSelected(!!hasCurrentCatalog);
            }}
          />
          <Modal
            visible={showLoginModal}
            backButton={() => {
              lurkerMetrics('click', 'login_nao', {
                object_name: 'form_login',
              });
              setShowLoginModal(false);
            }}
            nextButton={async () => {
              lurkerMetrics('click', 'login_sim', {
                object_name: 'form_login',
              });
              try {
                const { token } = await getLoginToken('form');
                (
                  window as any
                ).location = `https://conta.olx.com.br/acesso?returnToToken=${token}`;
              } catch {
                lurkerMetrics('error', 'login_error', {
                  object_name: 'form_login',
                });
              }
            }}
            title="Você consegue acessar sua conta?"
          >
            Precisamos que você faça login para prosseguir para o atendimento
          </Modal>
        </ContactUsContainer>
      )}
    </>
  );
};

ContactUs.getInitialProps = async (ctx: any) => {
  const apolloClient = initApolloClient();

  const { query } = ctx;
  const { data } = await apolloClient.query({
    query: CatalogsQuery,
    variables: { sort: 'position' },
  });

  return {
    ...data,
    ...query,
    initialApolloState: apolloClient.cache.extract(),
  };
};
export default ContactUs;
